import { useEffect, useState } from "react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  LimeInputLabel,
  LimeInputLabelProps,
} from "@/Components/NextBase/LimeInputLabel";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";
import { cn } from "@/utils";
import { TIME_OFF_TYPE_EMOJIS } from "./timeOffEmojis";

type CompactEmoji = {
  unicode: string;
  label: string;
  tags?: string[];
  group?: number;
};

/**
 * Emojibase's "component" group is skin-tone and hair modifiers, and the
 * entries with no group at all are the regional indicators flags are built
 * from. Neither is a mark anyone would label a time-off type with, and both
 * render as bare boxes in the grid.
 */
function isLabellable(emoji: CompactEmoji) {
  return emoji.group != null && emoji.group !== 2;
}

function groupLabel(group: number) {
  switch (group) {
    case 0:
      return t`Obrazi in čustva`;
    case 1:
      return t`Ljudje`;
    case 3:
      return t`Živali in narava`;
    case 4:
      return t`Hrana in pijača`;
    case 5:
      return t`Potovanja in kraji`;
    case 6:
      return t`Dejavnosti`;
    case 7:
      return t`Predmeti`;
    case 8:
      return t`Simboli`;
    default:
      return t`Zastave`;
  }
}

function groupByCategory(emojis: CompactEmoji[]): [number, CompactEmoji[]][] {
  const byGroup = new Map<number, CompactEmoji[]>();

  for (const emoji of emojis) {
    const group = emoji.group as number;
    const bucket = byGroup.get(group);
    if (bucket) {
      bucket.push(emoji);
    } else {
      byGroup.set(group, [emoji]);
    }
  }

  return [...byGroup].sort(([a], [b]) => a - b);
}

function matches(emoji: CompactEmoji, query: string) {
  return (
    emoji.label.toLowerCase().includes(query) ||
    emoji.tags?.some((tag) => tag.includes(query)) === true
  );
}

export const EmojiPickerGrid = ({
  value,
  onChange,
  fallback,
  wrapperClassName,
  ...props
}: {
  value?: string | null;
  onChange: (emoji: string) => void;
  /** Shown while no emoji is picked: what the type will display without one. */
  fallback: string;
  wrapperClassName?: string;
} & LimeInputLabelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [emojis, setEmojis] = useState<CompactEmoji[] | null>(null);

  // ~400KB of labels and tags, so it is a chunk of its own that only the
  // person who opens the picker ever downloads. The curated row below renders
  // meanwhile, which is what this field offered before search existed.
  useEffect(() => {
    if (!isOpen || emojis != null) return;

    let cancelled = false;
    import("emojibase-data/en/compact.json")
      .then((module) => {
        if (!cancelled) setEmojis(module.default as CompactEmoji[]);
      })
      .catch((error) => {
        sentryLog.error(
          "Emoji data failed to load",
          describeErrorForLog(error),
        );
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, emojis]);

  const search = query.trim().toLowerCase();
  const usable = (emojis ?? []).filter(isLabellable);
  const results = search
    ? usable.filter((emoji) => matches(emoji, search))
    : usable;

  const grouped = search ? [] : groupByCategory(results);

  return (
    <div className={cn("flex flex-col", wrapperClassName)}>
      <LimeInputLabel {...props} />

      <Popover
        placement="bottom-start"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <PopoverTrigger>
          <Button
            className="flex h-10 w-14 items-center justify-center text-xl"
            variant="bordered"
            type="button"
          >
            {value || fallback}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[336px] p-0">
          <div className="w-full p-2">
            <LimeInput
              size="sm"
              placeholder={t`Išči`}
              value={query}
              onValueChange={setQuery}
              isClearable
              onClear={() => setQuery("")}
            />
          </div>

          <div className="max-h-[300px] w-full overflow-y-auto px-2 pb-2">
            {!search && (
              <EmojiSection
                heading={t`Predlagano`}
                emojis={TIME_OFF_TYPE_EMOJIS.map((unicode) => ({
                  unicode,
                  label: unicode,
                }))}
                value={value}
                onChange={onChange}
              />
            )}

            {emojis == null && (
              <p className="text-tiny text-default-400 px-1 py-2">
                <Trans>Nalaganje …</Trans>
              </p>
            )}

            {search && results.length === 0 && emojis != null && (
              <p className="text-tiny text-default-400 px-1 py-2">
                <Trans>Ni zadetkov.</Trans>
              </p>
            )}

            {search ? (
              <EmojiSection
                emojis={results}
                value={value}
                onChange={onChange}
              />
            ) : (
              grouped.map(([group, groupEmojis]) => (
                <EmojiSection
                  key={group}
                  heading={groupLabel(group)}
                  emojis={groupEmojis}
                  value={value}
                  onChange={onChange}
                />
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/**
 * Plain buttons rather than the HeroUI one: this renders the whole emoji set at
 * once, and the component's ripple and focus machinery is what makes that cost
 * anything.
 */
function EmojiSection({
  heading,
  emojis,
  value,
  onChange,
}: {
  heading?: string;
  emojis: { unicode: string; label: string }[];
  value?: string | null;
  onChange: (emoji: string) => void;
}) {
  if (emojis.length === 0) return null;

  return (
    <div className="flex flex-col">
      {heading && (
        <p className="text-tiny text-default-400 px-1 pt-2 pb-1 font-medium">
          {heading}
        </p>
      )}
      <div className="grid grid-cols-8 gap-0.5">
        {emojis.map((emoji) => (
          <button
            key={emoji.unicode}
            type="button"
            aria-label={emoji.label}
            title={emoji.label}
            onClick={() => onChange(emoji.unicode)}
            className={cn(
              "hover:bg-default-100 flex h-9 items-center justify-center rounded-md text-lg",
              value === emoji.unicode && "bg-primary-100",
            )}
          >
            {emoji.unicode}
          </button>
        ))}
      </div>
    </div>
  );
}
